import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom'; // Import useNavigate
import Cookies from 'js-cookie';
import { BASE_URL } from '../config/constants';
function FinalTest() {
	const { id: courseId } = useParams(); // Get the course ID from the URL
	const token = Cookies.get('token');
	const navigate = useNavigate(); // Initialize useNavigate
	const [finalTest, setFinalTest] = useState(null);
	const [loading, setLoading] = useState(true);
	const [selectedAnswers, setSelectedAnswers] = useState({}); // Store user's selected answers
	const [submitted, setSubmitted] = useState(false); // Track if the test has been submitted
	const [score, setScore] = useState(0); // Track the user's score
	const [showCertificateButton, setShowCertificateButton] = useState(false); // Track if the certificate button should be shown
	const baseUrl = BASE_URL; // Fetch the final test for the course
	useEffect(() => {
		const fetchFinalTest = async () => {
			try {
				const response = await fetch(
					`${baseUrl}/api/v1/final-test/course/${courseId}`,
					{
						method: 'GET',
						headers: {
							Authorization: `Bearer ${token}`,
							'Content-Type': 'application/json',
						},
					}
				);

				if (response.ok) {
					const data = await response.json();
					setFinalTest(data[0]); // Assuming only one final test exists per course
				} else {
					console.error('Failed to fetch final test');
				}
			} catch (error) {
				console.error('Error fetching final test:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchFinalTest();
	}, [courseId, token]);

	// Handle answer selection
	const handleAnswerSelection = (questionIndex, selectedOptionIndex) => {
		setSelectedAnswers((prev) => ({
			...prev,
			[questionIndex]: selectedOptionIndex,
		}));
	};

	// Handle test submission
	const handleSubmit = () => {
		let correctAnswers = 0;

		finalTest.questions.forEach((question, index) => {
			if (selectedAnswers[index] === question.correctAnswer) {
				correctAnswers++;
			}
		});

		const userScore = (correctAnswers / finalTest.questions.length) * 100; // Calculate percentage
		setScore(userScore);
		setSubmitted(true);

		// Show the "Get Certificate" button if the score is more than 70%
		if (userScore > 70) {
			setShowCertificateButton(true);
		}
	};

	// Handle navigation to the certificate page
	const handleGetCertificate = () => {
		navigate(`/my-learning/${courseId}/certificate`); // Navigate to the dynamic certificate page
	};

	// Handle resetting the exam
	const handleResetExam = () => {
		setSelectedAnswers({}); // Reset selected answers
		setSubmitted(false); // Reset submission state
		setScore(0); // Reset score
		setShowCertificateButton(false); // Hide the certificate button
	};

	// Display loading state
	if (loading) {
		return (
			<div className='h-[80vh] flex justify-center items-center text-xl md:text-4xl text-main'>
				Loading...
			</div>
		);
	}

	// Display error if no final test is found
	if (!finalTest) {
		return (
			<div className='h-[80vh] flex justify-center items-center text-xl md:text-4xl text-main'>
				No final test found for this course.
			</div>
		);
	}

	return (
		<div className='p-6'>
			<h1 className='text-2xl font-bold mb-4'>
				Final Test: {finalTest.course.name}
			</h1>

			{/* Display Questions */}
			<div className='space-y-4'>
				{finalTest.questions.map((question, questionIndex) => (
					<div
						key={questionIndex}
						className='p-4 border border-gray-300 rounded-lg'>
						<h2 className='text-lg font-semibold mb-2'>
							{questionIndex + 1}. {question.question}
						</h2>
						<div className='space-y-2'>
							{question.options.map((option, optionIndex) => (
								<div
									key={optionIndex}
									className='flex items-center'>
									<input
										type='radio'
										name={`question-${questionIndex}`}
										value={optionIndex}
										checked={selectedAnswers[questionIndex] === optionIndex}
										onChange={() =>
											handleAnswerSelection(questionIndex, optionIndex)
										}
										className='mr-2'
										disabled={submitted} // Disable inputs after submission
										id={optionIndex}
									/>
									<label htmlFor={optionIndex}>{option}</label>
								</div>
							))}
						</div>
						{submitted && (
							<p className='mt-2 text-sm'>
								Correct Answer:{' '}
								<strong>{question.options[question.correctAnswer]}</strong>
							</p>
						)}
					</div>
				))}
			</div>

			{/* Submit Button */}
			{!submitted && (
				<button
					onClick={handleSubmit}
					className='mt-4 bg-main text-white px-4 py-2 rounded-md hover:bg-secondary'>
					Submit Final Test
				</button>
			)}

			{/* Display Score */}
			{submitted && (
				<div className='mt-4 p-4 bg-green-100 rounded-md'>
					<h2 className='text-lg font-semibold'>
						Your Score: {score.toFixed(2)}%{' '}
						{/* Display score as a percentage */}
					</h2>
					<p className='text-sm'>
						{score > 70
							? 'Congratulations! You passed the test! 🎉'
							: 'Keep practicing to improve your score! 💪'}
					</p>
				</div>
			)}

			{/* Show "Get Certificate" button if the user scored more than 70% */}
			{showCertificateButton && (
				<button
					onClick={handleGetCertificate}
					className='mt-4 bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600'>
					Get Certificate
				</button>
			)}

			{/* Show "Reset Exam" button if the user scored less than 70% */}
			{submitted && score <= 70 && (
				<button
					onClick={handleResetExam}
					className='mt-4 bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600'>
					Reset Exam
				</button>
			)}
		</div>
	);
}

export default FinalTest;
